/**
 * Responsible Form Modal Component
 * Modal for creating/editing company responsibles
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  CompanyResponsible,
  CompanyResponsibleFormData,
  ResponsibleType,
  RESPONSIBLE_TYPE_LABELS,
} from '../../../../types/company';

// Form validation schema
const responsibleSchema = yup.object({
  responsibleType: yup
    .string()
    .required('Tipo de responsável é obrigatório')
    .oneOf(Object.values(ResponsibleType), 'Tipo inválido'),
  providerId: yup.string().notRequired(),
  professionalId: yup.string().notRequired(),
  startDate: yup.string().required('Data de início é obrigatória'),
  endDate: yup.string().notRequired(),
  notes: yup.string().notRequired(),
}).test('provider-or-professional', 'Informe um prestador ou profissional', function (value) {
  return !!value.providerId || !!value.professionalId;
});

interface ResponsibleFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CompanyResponsibleFormData) => Promise<void>;
  responsible?: CompanyResponsible;
  providers: Array<{ id: number; name: string }>;
  professionals: Array<{ id: number; name: string }>;
  /**
   * Responsáveis já carregados pela aba. Usados só para avisar que o novo
   * cadastro vai coexistir com os vigentes do mesmo tipo (#345).
   */
  existingResponsibles?: CompanyResponsible[];
  loading?: boolean;
  error?: string | null;
}

const ResponsibleFormModal: React.FC<ResponsibleFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  responsible,
  providers,
  professionals,
  existingResponsibles = [],
  loading = false,
  error,
}) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const isEditing = !!responsible;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CompanyResponsibleFormData>({
    resolver: yupResolver(responsibleSchema) as any,
    defaultValues: {
      responsibleType: '',
      providerId: '',
      professionalId: '',
      startDate: '',
      endDate: '',
      notes: '',
    },
  });

  const watchResponsibleType = watch('responsibleType');

  // Determine if provider or professional should be shown based on type
  const showProvider = [
    ResponsibleType.CLINIC_PROVIDER,
    ResponsibleType.LAB_PROVIDER,
    ResponsibleType.ENVIRONMENT_PROVIDER,
  ].includes(watchResponsibleType as ResponsibleType);

  const showProfessional = [
    ResponsibleType.MEDICAL_RESPONSIBLE,
    ResponsibleType.ENVIRONMENTAL_RESPONSIBLE,
    ResponsibleType.TECHNICAL_RESPONSIBLE,
    ResponsibleType.LEGAL_RESPONSIBLE,
    ResponsibleType.SPEECH_THERAPY_RESPONSIBLE,
  ].includes(watchResponsibleType as ResponsibleType);

  /**
   * Vigentes do mesmo tipo já cadastrados. Não bloqueiam nada: desde a #345
   * mais de um responsável do mesmo tipo pode vigorar ao mesmo tempo, e o
   * cadastro novo não encerra os anteriores. O aviso existe para que quem
   * cadastra saiba disso.
   */
  const coexistingResponsibles = useMemo(() => {
    if (!watchResponsibleType) return [];

    return existingResponsibles.filter(
      (item) =>
        item.responsibleType === watchResponsibleType &&
        !item.endDate &&
        item.id !== responsible?.id
    );
  }, [existingResponsibles, watchResponsibleType, responsible]);

  const describeResponsible = (item: CompanyResponsible): string => {
    const name =
      item.professional?.name ??
      item.provider?.tradeName ??
      item.provider?.legalName ??
      'Sem nome';
    const since = new Date(item.startDate).toLocaleDateString('pt-BR');

    return `${name} — desde ${since}`;
  };

  // Clear the irrelevant field when type changes
  useEffect(() => {
    if (showProvider) {
      setValue('professionalId', '');
    } else if (showProfessional) {
      setValue('providerId', '');
    }
  }, [watchResponsibleType, showProvider, showProfessional, setValue]);

  // Reset form when modal opens/closes or responsible changes
  useEffect(() => {
    if (open && responsible) {
      reset({
        responsibleType: responsible.responsibleType,
        providerId: responsible.providerId?.toString() || '',
        professionalId: responsible.professionalId?.toString() || '',
        startDate: responsible.startDate?.split('T')[0] || '',
        endDate: responsible.endDate?.split('T')[0] || '',
        notes: responsible.notes ?? '',
      });
    } else if (open && !responsible) {
      reset({
        responsibleType: '',
        providerId: '',
        professionalId: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        notes: '',
      });
    }
  }, [open, responsible, reset]);

  const handleFormSubmit = async (data: CompanyResponsibleFormData) => {
    setSubmitLoading(true);
    try {
      await onSubmit(data);
      onClose();
    } catch (err) {
      // Error handling is done by parent component
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleClose = () => {
    if (!submitLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Editar Responsável' : 'Novo Responsável'}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Responsible Type */}
            <Grid size={12}>
              <Controller
                name="responsibleType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.responsibleType}>
                    <InputLabel>Tipo de Responsável *</InputLabel>
                    <Select {...field} label="Tipo de Responsável *">
                      <MenuItem value="">Selecione...</MenuItem>
                      {/* Registro legado com tipo "Prestador *": mostra o valor atual (desabilitado) p/ não ficar em branco */}
                      {showProvider && watchResponsibleType && (
                        <MenuItem value={watchResponsibleType} disabled>
                          {RESPONSIBLE_TYPE_LABELS[watchResponsibleType as ResponsibleType]} (legado)
                        </MenuItem>
                      )}
                      {Object.entries(RESPONSIBLE_TYPE_LABELS)
                        // Tipos "Prestador *" não são mais selecionáveis (mantidos no enum só p/ registros antigos)
                        .filter(([value]) => ![
                          ResponsibleType.CLINIC_PROVIDER,
                          ResponsibleType.LAB_PROVIDER,
                          ResponsibleType.ENVIRONMENT_PROVIDER,
                        ].includes(value as ResponsibleType))
                        .map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                    </Select>
                    {errors.responsibleType && (
                      <FormHelperText>{errors.responsibleType.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Aviso de coexistência (#345): cadastrar não encerra os anteriores */}
            {!isEditing && coexistingResponsibles.length > 0 && (
              <Grid size={12}>
                <Alert severity="info">
                  Já existe responsável vigente deste tipo. O novo cadastro passa a
                  coexistir com ele — para encerrar o anterior, use a ação Encerrar na
                  lista.
                  <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                    {coexistingResponsibles.map((item) => (
                      <li key={item.id}>{describeResponsible(item)}</li>
                    ))}
                  </Box>
                </Alert>
              </Grid>
            )}

            {/* Provider (shown for ENVIRONMENT_PROVIDER) */}
            {showProvider && (
              <Grid size={12}>
                <Controller
                  name="providerId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.providerId}>
                      <InputLabel>Prestador</InputLabel>
                      <Select {...field} label="Prestador">
                        <MenuItem value="">Selecione...</MenuItem>
                        {providers.map((provider) => (
                          <MenuItem key={provider.id} value={provider.id.toString()}>
                            {provider.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.providerId && (
                        <FormHelperText>{errors.providerId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            {/* Professional (shown for coordinators and responsibles) */}
            {showProfessional && (
              <Grid size={12}>
                <Controller
                  name="professionalId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.professionalId}>
                      <InputLabel>Profissional</InputLabel>
                      <Select {...field} label="Profissional">
                        <MenuItem value="">Selecione...</MenuItem>
                        {professionals.map((professional) => (
                          <MenuItem key={professional.id} value={professional.id.toString()}>
                            {professional.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.professionalId && (
                        <FormHelperText>{errors.professionalId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            {/* Start Date */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="date"
                    label="Data de Início *"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.startDate}
                    helperText={errors.startDate?.message}
                  />
                )}
              />
            </Grid>

            {/* End Date */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="date"
                    label="Data de Fim"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.endDate}
                    helperText={errors.endDate?.message}
                  />
                )}
              />
            </Grid>

            {/* Notes */}
            <Grid size={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    label="Observacoes"
                    error={!!errors.notes}
                    helperText={errors.notes?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitLoading || loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitLoading || loading}
          >
            {submitLoading || loading ? (
              <CircularProgress size={24} />
            ) : isEditing ? (
              'Salvar'
            ) : (
              'Adicionar'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ResponsibleFormModal;
